<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import lottie from 'lottie-web'

import animation from '@/components/ContestGame/assets/scratch-card.json'
import card from '@/components/ContestGame/assets/card.svg'

import type {
  ContestColor,
  ContestGameReward,
  FormattedContestGameReward,
  GameContest,
  Merchant,
} from '@/components/ContestGame/contestGame'

const props = defineProps<{
  color: ContestColor
  contestGame: GameContest
  coverImage: string
  logo: string
  merchant: Merchant
  modelValue: boolean
  reward: ContestGameReward | null
  rewards: FormattedContestGameReward[]
  webAppUrl: string
}>()

const emit = defineEmits<{
  (e: 'completed'): void
}>()

const canvas = ref(null)
const isDrawing = ref(false)
const isScratched = ref(false)
const lastPoint = ref({ x: 0, y: 0 })
const canvasSize = ref(304)
const lottieContainer = ref(null)
const lottieAnimation = ref(null)
const isCanvasReady = ref(false)

const initCanvas = () => {
  const ctx = canvas.value.getContext('2d')
  const scratchImage = new Image()
  scratchImage.crossOrigin = 'anonymous'
  scratchImage.src = props.coverImage || props.logo

  const screenWidth = window.innerWidth
  const paddingTotal = 24
  const maxSize = 400
  canvasSize.value = Math.min(screenWidth - paddingTotal, maxSize)

  canvas.value.width = canvasSize.value
  canvas.value.height = canvasSize.value

  scratchImage.onload = () => {
    const imgWidth = scratchImage.width
    const imgHeight = scratchImage.height
    const ratio = Math.max(canvasSize.value / imgWidth, canvasSize.value / imgHeight)
    const newWidth = imgWidth * ratio
    const newHeight = imgHeight * ratio
    const offsetX = (canvasSize.value - newWidth) / 2
    const offsetY = (canvasSize.value - newHeight) / 2

    ctx.drawImage(scratchImage, offsetX, offsetY, newWidth, newHeight)

    const svgImage = new Image()
    svgImage.crossOrigin = 'anonymous'

    svgImage.onload = () => {
      ctx.drawImage(svgImage, 0, 0, canvasSize.value, canvasSize.value)
      isCanvasReady.value = true
    }
  }

  scratchImage.onerror = () => {
    const img = new Image()

    img.src = card
    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvasSize.value, canvasSize.value)
      isCanvasReady.value = true
    }
  }

  ctx.globalCompositeOperation = 'source-over'
}

const handleMouseDown = (e) => {
  e.preventDefault()
  isDrawing.value = true
  lastPoint.value = getMousePosition(e)

  if (lottieAnimation.value) lottieAnimation.value.destroy()
}

const handleMouseMove = (e) => {
  if (!isDrawing.value) return
  const ctx = canvas.value.getContext('2d')
  const currentPoint = getMousePosition(e)
  const dist = distanceBetween(lastPoint.value, currentPoint)
  const angle = angleBetween(lastPoint.value, currentPoint)

  for (let i = 0; i < dist; i++) {
    const x = lastPoint.value.x + Math.sin(angle) * i - 15
    const y = lastPoint.value.y + Math.cos(angle) * i - 15
    ctx.globalCompositeOperation = 'destination-out'
    ctx.beginPath()
    ctx.arc(x, y, 20, 0, Math.PI * 2)
    ctx.fill()
  }

  lastPoint.value = currentPoint
  checkScratchCompletion()
}

const handleMouseUp = () => {
  isDrawing.value = false
}

const getMousePosition = (e) => {
  const rect = canvas.value.getBoundingClientRect()
  return {
    x: (e.pageX || e.touches[0].clientX) - rect.left,
    y: (e.pageY || e.touches[0].clientY) - rect.top,
  }
}

const distanceBetween = (point1, point2) => {
  return Math.sqrt(Math.pow(point2.x - point1.x, 2) + Math.pow(point2.y - point1.y, 2))
}

const angleBetween = (point1, point2) => {
  return Math.atan2(point2.y - point1.y, point2.x - point1.x)
}

const checkScratchCompletion = () => {
  const ctx = canvas.value.getContext('2d')

  const centerSize = Math.floor(canvasSize.value * 0.3)
  const startX = Math.floor((canvasSize.value - centerSize) / 2)
  const startY = Math.floor((canvasSize.value - centerSize) / 2)

  const centerPixels = ctx.getImageData(startX, startY, centerSize, centerSize).data
  const totalCenterPixels = centerPixels.length / 4
  let scratchedCenterPixels = 0

  for (let i = 3; i < centerPixels.length; i += 4) {
    if (centerPixels[i] === 0) scratchedCenterPixels++
  }

  const scratchedPercentage = (scratchedCenterPixels / totalCenterPixels) * 100

  if (scratchedPercentage >= 95 && !isScratched.value) {
    let radius = 0
    const maxRadius = Math.sqrt(Math.pow(canvasSize.value, 2) + Math.pow(canvasSize.value, 2)) / 2
    const centerX = canvasSize.value / 2
    const centerY = canvasSize.value / 2

    const animate = () => {
      ctx.globalCompositeOperation = 'destination-out'
      ctx.beginPath()
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2)
      ctx.fill()

      radius += maxRadius / 60

      if (radius <= maxRadius) {
        requestAnimationFrame(animate)
      }
    }

    isScratched.value = true
    emit('completed')
    animate()
  }
}

const initLottie = () => {
  lottieAnimation.value = lottie.loadAnimation({
    container: lottieContainer.value,
    renderer: 'svg',
    loop: true,
    autoplay: true,
    animationData: animation,
  })
}

const style = computed(() => {
  return {
    width: `${canvasSize.value}px`,
    height: `${canvasSize.value}px`,
  }
})

onMounted(() => {
  initCanvas()
})

onUnmounted(() => {
  if (lottieAnimation.value) {
    lottieAnimation.value.destroy()
  }
})

watch(
  () => props.modelValue,
  () => {
    if (props.modelValue) {
      initLottie()
    }
  },
)
</script>

<template>
  <div class="scratch-card h-[calc(100vh-5vh)] w-full pt-[75px] duration-300">
    <div
      :style="style"
      :class="[
        'scratch-card-container relative top-[5vh] z-10 mx-auto translate-y-[-5vh] rounded-3xl',
        {
          'animate-scratch-card': modelValue,
        },
      ]"
    >
      <div
        v-if="isCanvasReady"
        :style="{
          background: reward?.color,
        }"
        class="card-glow absolute inset-0 z-0 flex h-full w-full flex-col items-center justify-center rounded-3xl bg-white text-center"
      >
        <div class="p-4 text-2xl font-extrabold">
          <p :style="{ color: reward ? reward?.textColor : contestGame.losingTileColor }">
            {{ reward ? reward?.name : contestGame.losingTileText }}
          </p>
        </div>
      </div>

      <canvas
        ref="canvas"
        class="relative z-10 rounded-3xl"
        @mousedown="handleMouseDown"
        @touchstart="handleMouseDown"
        @mousemove="handleMouseMove"
        @touchmove="handleMouseMove"
        @mouseup="handleMouseUp"
        @touchend="handleMouseUp"
      />
      <div
        class="shine-overlay pointer-events-none absolute inset-0 left-0 top-0 z-20 rounded-3xl"
      />
      <div
        ref="lottieContainer"
        class="pointer-events-none absolute left-0 top-0 z-30 h-full w-full"
      />
    </div>
  </div>
</template>

<style scoped>
.scratch-card-container .shine-overlay {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0) 30%,
    rgba(255, 255, 255, 0.4) 50%,
    rgba(255, 255, 255, 0) 70%
  );
  background-size: 300% 300%;
  animation: shine 3s infinite linear;
}

@keyframes shine {
  0% {
    background-position: 0% 0%;
  }

  100% {
    background-position: 100% 100%;
  }
}

.scratch-card-container .card-glow {
  animation: glow3d 4s ease-in-out infinite;
  transform-style: preserve-3d;
  perspective: 1000px;
}

.scratch-card-container.animate-scratch-card .card-glow {
  transform-style: preserve-3d;
  perspective: 1000px;
}

@keyframes glow3d {
  0% {
    box-shadow:
      10px 10px 20px v-bind('color.fromColor'),
      -10px -10px 20px v-bind('color.toColor');
    transform: rotateX(3deg) rotateY(3deg);
  }

  25% {
    box-shadow:
      -10px 10px 20px v-bind('color.fromColor'),
      10px -10px 20px v-bind('color.toColor');
    transform: rotateX(-3deg) rotateY(3deg);
  }

  50% {
    box-shadow:
      -10px -10px 20px v-bind('color.fromColor'),
      10px 10px 20px v-bind('color.toColor');
    transform: rotateX(-3deg) rotateY(-3deg);
  }

  75% {
    box-shadow:
      10px -10px 20px v-bind('color.fromColor'),
      -10px 10px 20px v-bind('color.toColor');
    transform: rotateX(3deg) rotateY(-3deg);
  }

  100% {
    box-shadow:
      10px 10px 20px v-bind('color.fromColor'),
      -10px -10px 20px v-bind('color.toColor');
    transform: rotateX(3deg) rotateY(3deg);
  }
}

.animate-scratch-card {
  animation: scratch-card 1s ease-in-out forwards;
}

@keyframes scratch-card {
  from {
    top: 32px;
    transform: translateY(-32px);
  }

  to {
    top: 50%;
    transform: translateY(-50%);
  }
}
</style>
